import { z } from 'zod';
import { ThemingFontDefSchema } from '~schemas/settings';

/**
 * The API has two ways of saying "not branded": null, and the empty string that
 * the original /v2/branding spec showed as its placeholder value. Only null
 * reaches the per-field fallback in `buildThemingFromBranding`, so a blank would
 * survive the merge and then be silently dropped by `applyTheming` — leaving the
 * static stylesheet value in place instead of the default theme, which is wrong
 * for a dark-mode tenant. Collapsing blanks to null here puts both shapes on the
 * same path.
 */
const blankToNull = z
  .string()
  .transform((value) => value.trim() || null)
  .nullable()
  .optional();

export const BrandingColoursSchema = z.object({
  brand: z
    .object({
      primary: blankToNull,
      surface: blankToNull,
    })
    .nullable()
    .optional(),
  text: z
    .object({
      heading: blankToNull,
      body: blankToNull,
    })
    .nullable()
    .optional(),
});

// A font with no family cannot be resolved into a stylesheet URL, so drop the
// whole definition and let the default typeface fall back in.
const BrandingFontSchema = ThemingFontDefSchema.transform((font) => (font.family.trim() ? { ...font, family: font.family.trim() } : null));

export const BrandingFontsSchema = z.object({
  heading: BrandingFontSchema.nullable().optional(),
  body: BrandingFontSchema.nullable().optional(),
});

export const BrandingAssetsSchema = z.object({
  logo: z.object({ url: z.string() }).nullable().optional(),
  banner: z.object({ url: z.string() }).nullable().optional(),
});

export const BrandingSchema = z.object({
  colours: BrandingColoursSchema.nullable().optional(),
  fonts: BrandingFontsSchema.nullable().optional(),
  assets: BrandingAssetsSchema.nullable().optional(),
  dark_mode: z.boolean().default(false),
});

export type Branding = z.infer<typeof BrandingSchema>;
